import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../utils/useUserProfile';
import { useClubFitting } from '../utils/useClubFitting';
import RecommendationCards from '../components/RecommendationCards';
import AIAnalysis from '../components/AIAnalysis';
import AnalysisModal from '../components/profile/AnalysisModal';
import TopBar from '../components/TopBar';
import SiteFooter from '../components/Footer';
import ProfileModal from '../components/profile/ProfileModal';
import { UserProfile } from '../utils/interfaces';
import { analyzeUserProfile } from '../utils/useUserProfile';
import { GOLFER_PRODUCT_RESULTS_CTAS, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS } from '../utils/golfFittingCtas';
import './ClubFittingMobile.css';

const clubSelectionConfig = {
  Driver: {
    secondaryDropdowns: [
      {
        label: 'Common Miss',
        options: [
          'Select an answer',
          'Slice (pushes right for righties)',
          'Hook (draws left for righties)',
          'Push / Block (starts right, stays right)',
          'Pull (starts left, stays left)',
          'Spin / Ballooning (high flight, lost distance)',
          'No Launch (low, diving shots)'
        ],
      },
      {
        label: 'Primary Goal',
        options: [
          'Select an answer',
          'Max Distance (15+ yards more)',
          'Fairway Control (straighter, tighter misses)',
          'More Forgiveness (more playable miss-hits)',
          'Lower Spin (piercing, boring flight)',
          'Higher Launch (more carry and airtime)'
        ],
      },
      {
        label: 'Swing Path',
        options: [
            'Select an answer',
            'Out-to-In (classic slice shape)',
            'In-to-Out (classic draw/hook shape)',
            'Steep / Downward (more downward strike, more spin)',
            'Smooth & Level (balanced, sweeping motion)',
        ],
      },
    ],
  },
  'Fairway Woods': {
    secondaryDropdowns: [
      {
        label: 'Common Miss',
        options: [
          'Select an answer',
          'Low On The Face / Weak Contact',
          'High On The Face / Thin Strike',
          'Slice (starts right, fades away)',
          'Hook (starts left, bends hard left)',
          'Too Much Spin / Ballooning',
          'No Carry / Low Launch',
        ],
      },
      {
        label: 'Primary Goal',
        options: [
          'Select an answer',
          'More Distance Off The Tee',
          'More Fairway Control',
          'Safer Misses / More Forgiveness',
          'Higher Launch / Easier Carry',
          'Lower Spin / More Roll',
        ],
      },
      {
        label: 'Typical Lie / Setup',
        options: [
          'Select an answer',
          'Tee Box / Launch Conditions',
          'Fairway / Tight Lies',
          'Light Rough',
          'Heavy Rough',
          'Uneven / Off-Center Lies',
        ],
      },
    ],
  },
  Utilities: {
    secondaryDropdowns: [
      {
        label: 'Common Struggle',
        options: [
          'Select an answer',
          'Misses Left / Right Off The Face',
          'Low Launch / No Carry',
          'Hard To Get Airborne',
          'Fat Shots / Turf Contact',
          'Bad Distance Gapping',
        ],
      },
      {
        label: 'Main Goal',
        options: [
          'Select an answer',
          'More Forgiveness',
          'More Distance',
          'Easier Launch',
          'Better Versatility',
          'Tighter Shot Control',
        ],
      },
      {
        label: 'Preferred Ball Flight',
        options: [
          'Select an answer',
          'High Launch / Easy Carry',
          'Lower Flight / Better Workability',
          'Straight And Forgiving',
          'Draw Bias',
          'Fade Bias',
        ],
      },
    ],
  },
  Iron: {
    secondaryDropdowns: [
      {
        label: 'Common Struggle',
        options: [
          'Select an answer',
          'Fat Shots (striking ground before the ball)',
          'Thin / Topped (low, skull-like strikes)',
          'Distance Loss (misses cost too many yards)',
          'Inconsistent Distance (big swings, uneven results)',
          'Shanks (misses off the hosel)',
        ],
      },
      {
        label: 'Main Goal',
        options: [
          'Select an answer',
          'Max Forgiveness (more straight shots on miss-hits)',
          'More Distance (7-iron +10–15 yards)',
          'Higher Launch (more carry and stopping power)',
          'Shot Shaping (better left/right control)',
          'Better Turf Contact (cleaner strikes through grass)',
        ],
      },
      {
        label: 'Preferred Head Style',
        options: [
          'Select an answer',
          'Big & Forgiving (larger head, thicker topline)',
          'Modern & Sleek (clean look, hidden tech)',
          'Balanced / Traditional (mid-size head, moderate topline)',
          'Classic Blade (compact, thinner, more precise)',
        ],
      },
    ],
  },
  Wedge: {
    secondaryDropdowns: [
      {
        label: 'Common Struggle',
        options: [
          'Select an answer',
          'The "Chili-Dip" / Chunk (Turf first, ball moves 2 feet)',
          'The "Skull" / Blade (Blade across green into bunker)',
          'The Sand Trap (Stuck in bunkers, need 2–3 shots)',
          'No Stopping Power (Ball rolls forever on green)',
          'Tight Lies (Hard to get under ball from tight lies)',
        ],
      },
      {
        label: 'Course Conditions',
        options: [
          'Select an answer',
          'Soft / Lush / Wet (Fluffy grass, soft sand — High Bounce)',
          'Firm / Dry / Hardpan (Tight grass, compacted sand — Low Bounce)',
          'Average / Normal (Mixed conditions — Mid Bounce)',
          'Variable / Fast Greens (Changing surfaces, extra spin needed)',
        ],
      },
      {
        label: 'Divot Type',
        options: [
          'Select an answer',
          'Deep & Chunky (Dig deep and take big divots)',
          'Shallow & Clean (Barely takes a divot)',
          'Moderate / Standard (A normal, average divot)',
          'No Divot At All (Sweep it clean off the turf)',
        ],
      },
    ],
  },
  Putter: {
    secondaryDropdowns: [
      {
        label: 'Common Frustration',
        options: [
          'Select an answer',
          'Three-Putting (Distance control on long putts)',
          'Missing Short Putts (Pushed/pulled inside 5 feet)',
          'Bad Alignment (Struggling to aim the face)',
          'Inconsistent Strike (Toe/heel strikes roll short)',
          'Speed Control (No feel for pace on fast / slow greens)',
        ],
      },
      {
        label: 'Stroke Path',
        options: [
          'Select an answer',
          'Straight Back, Straight Through (Straight stroke)',
          'Slight Arc (Slightly arcing stroke)',
          'Strong Arc (Strong arc stroke)',
          'I Have No Idea (New to putting—recommend a safe mallet)',
        ],
      },
      {
        label: 'Preferred Feel',
        options: [
          'Select an answer',
          'Soft & Muted (Soft feel with a quieter, dampened impact)',
          'Firm & Crisp (Solid feel with sharp feedback at impact)',
          'Balanced & Consistent (Steady ball speed on off-center strikes)',
          'No Preference (Open to any feel as long as the ball rolls smoothly)',
        ],
      },
    ],
  },
  'Full Set': {
    secondaryDropdowns: [
      {
        label: 'Biggest Need',
        options: [
          'Select an answer',
          'More Distance Off The Tee (longer drives)',
          'Better Iron Consistency (more predictable yardages)',
          'More Green Control (better approach shots)',
          'Better Putting Performance (fewer 3-putts)',
          'Overall Bag Upgrade / Replacement (replace major gaps)',
        ],
      },
      {
        label: 'Primary Goal',
        options: [
          'Select an answer',
          'Best Value / Best Performance For The Money',
          'Maximum Forgiveness (stable on miss-hits)',
          'Distance + Launch (more carry and speed)',
          'Better Feel / Feedback (more responsive feel)',
          'Balanced All-Around Setup (mix of distance, feel, and forgiveness)',
          'Improved Consistency (more repeatable contact and results)',
          'Easy To Use / Confidence Boost (simple, repeatable setup)',
        ],
      },
      {
        label: 'Preferred Budget',
        options: [
          'Select an answer',
          'Budget Friendly / Best Value',
          'Mid Range / Good Balance',
          'Premium / Performance Upgrade',
          'No Budget Limit / Best Available Setup',
        ],
      },
    ],
  },
} as const;

const buildCtaSequence = () => {
  const rest = [...GOLFER_PRODUCT_RESULTS_CTAS.slice(1)];

  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  return [GOLFER_PRODUCT_RESULTS_CTAS[0], ...rest];
};

const ClubFittingQuiz: React.FC = () => {
  const navigate = useNavigate();
  const {
    userProfile,
    isLoggedIn,
    profileLoading,
    editableProfile,
    handleProfileChange,
    handleProfileSave,
    ensureEditableProfile,
  } = useUserProfile();
  const {
    recommendations,
    loading: recsLoading,
    error: recsError,
    overview,
    compare,
    fetchRecommendations,
  } = useClubFitting();

  const [submitted, setSubmitted] = useState(false);
  const [selectedClubType, setSelectedClubType] = useState<keyof typeof clubSelectionConfig>('Driver');
  const [selectedCondition, setSelectedCondition] = useState<'new' | 'used'>('new');
  const [secondarySelections, setSecondarySelections] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisRun, setAnalysisRun] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [activeProfileCategory, setActiveProfileCategory] = useState<string | null>('General');
  const [profileHash, setProfileHash] = useState<string | null>(null);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [ctaSequence, setCtaSequence] = useState<string[]>(() => buildCtaSequence());
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState<string | null>(null);

  const currentClubConfig = clubSelectionConfig[selectedClubType];

  useEffect(() => {
    if (!recsLoading) {
      return;
    }

    const interval = setInterval(() => {
      setLoadingTextIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;

        if (nextIndex >= ctaSequence.length) {
          const nextSequence = buildCtaSequence();
          setCtaSequence(nextSequence);
          return 0;
        }

        return nextIndex;
      });
    }, GOLFER_PRODUCT_RESULTS_CTA_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [recsLoading, ctaSequence.length]);

  useEffect(() => {
    if (recsLoading) {
      const nextSequence = buildCtaSequence();
      setCtaSequence(nextSequence);
      setLoadingTextIndex(0);
    }
  }, [recsLoading]);

  useEffect(() => {
    const cachedAnalysis = localStorage.getItem('profileAnalysis');
    const cachedProfileHash = localStorage.getItem('profileHash');

    if (cachedAnalysis && cachedProfileHash) {
      try {
        setAnalysis(JSON.parse(cachedAnalysis));
        setProfileHash(cachedProfileHash);
        setAnalysisRun(true);
      } catch (error) {
        console.error('Failed to restore cached analysis:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      const currentProfileHash = JSON.stringify(userProfile);

      if (profileHash && currentProfileHash !== profileHash) {
        setAnalysisRun(false);
        setAnalysis(null);
        localStorage.removeItem('profileAnalysis');
        localStorage.removeItem('profileHash');
      }

      setProfileHash(currentProfileHash);
    }
  }, [userProfile, profileHash]);

  const getProfileMenuCounts = (profile: UserProfile | null) => {
    const counts: Record<string, number> = {};
    if (!profile) return counts;

    const categories = ['Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'] as const;

    categories.forEach((category) => {
      if (category === 'Driver') {
        counts[category] = profile.Driver ? 1 : 0;
      } else if (category === 'Fairway Woods') {
        const woodFields = ['3-Wood', '4-Wood', '5-Wood', '6-Wood', '7-Wood', '8-Wood', '9-Wood'];
        counts[category] = woodFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Utilities') {
        const utilityFields = ['1-Iron', '2-Iron', '2-Hybrid', '3-Hybrid', '4-Hybrid', '5-Hybrid', '6-Hybrid'];
        counts[category] = utilityFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Irons') {
        const ironFields = ['3-Iron', '4-Iron', '5-Iron', '6-Iron', '7-Iron', '8-Iron', '9-Iron', 'Pitching Wedge'];
        counts[category] = ironFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Wedges') {
        const wedgeFields = ['Gap Wedge', 'Sand Wedge', 'Lob Wedge'];
        counts[category] = wedgeFields.filter((field) => {
          const value = profile[field as keyof UserProfile];
          return value !== undefined && value !== null && !(typeof value === 'string');
        }).length;
      } else if (category === 'Putter') {
        counts[category] = profile.Putter ? 1 : 0;
      } else if (category === 'Ball') {
        counts[category] = profile.Ball ? 1 : 0;
      }
    });

    return counts;
  };

  const profileMenuCounts = getProfileMenuCounts(userProfile ?? null);

  const profilePayload = useMemo<UserProfile>(() => {
    const baseProfile: UserProfile = userProfile ?? {
      general: {
        gender: 'Male',
        handedness: 'Right',
        handicap: null,
        playing_frequency: null,
        skill_level: null,
        preferred_brands: null,
        height_inches: null,
        wrist_to_floor_inches: null,
        swing_speed: null,
        attack_angle: null,
        launch_angle: null,
        spin_rate: null,
        swinging_type: null,
        tempo: null,
        course_conditions: null,
      },
    };

    return {
      ...baseProfile,
      general: {
        ...(baseProfile.general ?? {
          gender: 'Male',
          handedness: 'Right',
          height_inches: null,
          wrist_to_floor_inches: null,
          swing_speed: null,
          attack_angle: null,
          launch_angle: null,
          spin_rate: null,
          swinging_type: null,
          tempo: null,
          course_conditions: null,
        }),
        gender: baseProfile.general?.gender ?? 'Male',
        handedness: baseProfile.general?.handedness ?? 'Right',
        handicap: baseProfile.general?.handicap ?? null,
        playing_frequency: baseProfile.general?.playing_frequency ?? null,
        skill_level: baseProfile.general?.skill_level ?? null,
        preferred_brands: baseProfile.general?.preferred_brands ?? null,
        height_inches: baseProfile.general?.height_inches ?? null,
        wrist_to_floor_inches: baseProfile.general?.wrist_to_floor_inches ?? null,
        swing_speed: baseProfile.general?.swing_speed ?? null,
        attack_angle: baseProfile.general?.attack_angle ?? null,
        launch_angle: baseProfile.general?.launch_angle ?? null,
        spin_rate: baseProfile.general?.spin_rate ?? null,
        swinging_type: baseProfile.general?.swinging_type ?? null,
        tempo: baseProfile.general?.tempo ?? null,
        course_conditions: baseProfile.general?.course_conditions ?? null,
      },
    };
  }, [userProfile]);

  const handleSecondarySelection = (index: number, value: string) => {
    const key = `${selectedClubType}-${index}`;
    setSecondarySelections((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getFirstIncompleteProfileCategory = () => {
    const categories = ['Driver', 'Fairway Woods', 'Utilities', 'Irons', 'Wedges', 'Putter', 'Ball'] as const;

    const firstIncompleteCategory = categories.find((category) => {
      const count = profileMenuCounts[category] ?? 0;
      return count === 0;
    });

    if (firstIncompleteCategory) {
      return firstIncompleteCategory;
    }

    return 'General';
  };

  const handleOpenProfileModal = (category: string = 'General') => {
    ensureEditableProfile();
    setActiveProfileCategory(category);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setActiveProfileCategory(null);
  };

  const handleAnalyzeProfile = async () => {
    if (!userProfile) return;
    setAnalysisLoading(true);
    setIsAnalysisModalOpen(true);
    setAnalysis(null);

    try {
      const result = await analyzeUserProfile(userProfile);
      const currentProfileHash = JSON.stringify(userProfile);

      setAnalysis(result);
      setProfileHash(currentProfileHash);
      setAnalysisRun(true);
      localStorage.setItem('profileAnalysis', JSON.stringify(result));
      localStorage.setItem('profileHash', currentProfileHash);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleSaveProfileInModal = async () => {
    if (!editableProfile) return;

    try {
      await handleProfileSave();
      handleCloseProfileModal();
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const handleContinueNextCategory = async (category: string) => {
    if (!editableProfile) return;

    try {
      await handleProfileSave();
      setActiveProfileCategory(category);
      setIsProfileModalOpen(true);
    } catch (error) {
      console.error('Failed to advance profile:', error);
    }
  };

  const isDefaultSecondaryValue = (dropdown: { options: readonly string[] }, index: number) => {
    const selectedValue = secondarySelections[`${selectedClubType}-${index}`] ?? '';
    return !selectedValue || selectedValue === dropdown.options[0];
  };

  const handleSubmit = () => {
    const clubCategory = selectedClubType;

    const clubAnswersByLabel: Record<string, string> = {};
    currentClubConfig.secondaryDropdowns.forEach((dropdown, index) => {
      const selectedValue = secondarySelections[`${clubCategory}-${index}`] ?? dropdown.options[0];
      if (selectedValue === dropdown.options[0]) {
        return;
      }
      clubAnswersByLabel[dropdown.label] = selectedValue;
    });

    const profileByClubKey = (profilePayload as Record<string, Record<string, unknown>>)[clubCategory] ?? {};

    const finalProfilePayload: UserProfile = {
      ...profilePayload,
      general: {
        gender: userProfile?.general?.gender ?? 'Male',
        handedness: userProfile?.general?.handedness ?? 'Right',
        handicap: profilePayload.general?.handicap ?? null,
        playing_frequency: profilePayload.general?.playing_frequency ?? null,
        skill_level: profilePayload.general?.skill_level ?? null,
        preferred_brands: profilePayload.general?.preferred_brands ?? null,
        height_inches: profilePayload.general?.height_inches ?? null,
        wrist_to_floor_inches: profilePayload.general?.wrist_to_floor_inches ?? null,
        swing_speed: profilePayload.general?.swing_speed ?? null,
        attack_angle: profilePayload.general?.attack_angle ?? null,
        launch_angle: profilePayload.general?.launch_angle ?? null,
        spin_rate: profilePayload.general?.spin_rate ?? null,
        swinging_type: profilePayload.general?.swinging_type ?? null,
        tempo: profilePayload.general?.tempo ?? null,
        course_conditions: profilePayload.general?.course_conditions ?? null,
      },
      [clubCategory]: {
        ...profileByClubKey,
        ...clubAnswersByLabel,
      } as any,
    };

    const nextRequestSignature = JSON.stringify({
      profile: finalProfilePayload,
      clubType: selectedClubType,
      condition: selectedCondition,
      selections: secondarySelections,
    });

    if (lastSubmittedPayload && nextRequestSignature === lastSubmittedPayload) {
      setValidationError('Please update your player profile or game plan questions, then request new recommendations.');
      return;
    }

    setValidationError(null);
    setLastSubmittedPayload(nextRequestSignature);

    fetchRecommendations(
      finalProfilePayload,
      isLoggedIn,
      selectedClubType,
      profilePayload.general?.preferred_brands ?? undefined,
      undefined,
      selectedCondition,
      undefined,
    );
    setSubmitted(true);
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('messenger-conversation');
    localStorage.removeItem('messenger-visible');
    navigate('/');
  };

  return (
    <>
      <TopBar
        showSignOut={isLoggedIn}
        onSignOut={handleSignOut}
        onOpenProfileModal={handleOpenProfileModal}
        onOpenAnalysisModal={() => setIsAnalysisModalOpen(true)}
        onAnalyzeProfile={handleAnalyzeProfile}
        showGetMyBagScoreAction={Boolean(userProfile) && !analysisRun}
        showBagScoreAction={analysisRun && Boolean(analysis)}
        profileMenuCounts={profileMenuCounts}
      />
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        activeCategory={activeProfileCategory}
        editableProfile={editableProfile ?? null}
        handleProfileChange={handleProfileChange}
        handleProfileSave={handleSaveProfileInModal}
        loading={profileLoading}
        onContinueNextCategory={handleContinueNextCategory}
      />
      <AnalysisModal
        isOpen={isAnalysisModalOpen}
        onClose={() => setIsAnalysisModalOpen(false)}
        analysis={analysis}
        loading={analysisLoading}
      />
      <div className="mobile-clubfitting">
        <div className="mobile-clubfitting__card">
          <div className="mobile-clubfitting__card-header">
            <h2 className="nf-section-title" style={{ color: 'rgb(211, 47, 47)', margin: 0 }}>Club Fitting</h2>
            <span
              onClick={() => handleOpenProfileModal(getFirstIncompleteProfileCategory())}
              style={{
                color: '#dc2626',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.9rem',
                background: '#fff5f5',
                border: '1px solid rgba(220, 38, 38, 0.6)',
                borderRadius: '999px',
                padding: '0.12rem 0.45rem',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.18)',
                display: 'inline-block',
                lineHeight: 1.3,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.24)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.18)';
              }}
            >
              Player Profile
            </span>
          </div>
          <div className="mobile-clubfitting__filters" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="mobile-clubfitting__select-wrap">
              <select
                aria-label="Club category"
                value={selectedClubType}
                onChange={(event) => setSelectedClubType(event.target.value as keyof typeof clubSelectionConfig)}
              >
                {Object.keys(clubSelectionConfig).map((clubType) => (
                  <option key={clubType} value={clubType}>
                    {clubType}
                  </option>
                ))}
              </select>
            </div>

            <div className="mobile-clubfitting__select-wrap">
              <select
                aria-label="Condition"
                value={selectedCondition}
                onChange={(event) => setSelectedCondition(event.target.value as 'new' | 'used')}
              >
                <option value="new">Brand New</option>
                <option value="used">Pre-owned</option>
              </select>
            </div>
          </div>
          <p style={{ marginTop: '0.75rem', color: '#111827' }}>Tell us about your <i>{selectedClubType.toLowerCase()}</i> game:</p>
          <div className="mobile-clubfitting__field" style={{ marginTop: '0.75rem' }}>
            {currentClubConfig.secondaryDropdowns.map((dropdown, index) => {
              const isDefaultValue = isDefaultSecondaryValue(dropdown, index);
              const selectedValue = secondarySelections[`${selectedClubType}-${index}`] ?? '';

              return (
                <div
                  key={`${selectedClubType}-${dropdown.label}`}
                  className="mobile-clubfitting__select-wrap"
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 0.35fr) minmax(0, 2.1fr)', gap: '0.5rem', alignItems: 'center' }}
                >
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{dropdown.label}</span>
                  <select
                    aria-label={dropdown.label}
                    value={selectedValue}
                    onChange={(event) => handleSecondarySelection(index, event.target.value)}
                    style={{
                      backgroundColor: isDefaultValue ? '#d9dbde' : '#ffffff',
                      color: isDefaultValue ? '#403f3e' : '#111827',
                      borderColor: isDefaultValue ? '#d1d5db' : '#cbd5e1',
                      opacity: isDefaultValue ? 0.8 : 1,
                    }}
                  >
                    <option value="" disabled hidden>
                      Select an answer
                    </option>
                    {dropdown.options.slice(1).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mobile-clubfitting__actions">
          <button
            type="button"
            className="mobile-clubfitting__primary mobile-clubfitting__full"
            onClick={handleSubmit}
            disabled={recsLoading}
          >
            {recsLoading ? 'Loading Recommendations...' : 'Get Recommendations'}
          </button>
        </div>

        {(validationError || recsError) && (
          <div className="mobile-clubfitting__error" style={{ marginTop: '1rem' }}>
            {validationError ?? recsError}
          </div>
        )}

        {(overview || submitted || recommendations.length > 0) && (
          <div style={{ marginTop: '1.25rem' }}>
            <AIAnalysis overview={overview} hasFetched={submitted || recommendations.length > 0} loading={recsLoading} onOpenMessenger={() => undefined} />
          </div>
        )}

        {submitted || recommendations.length > 0 ? (
          <div style={{ marginTop: '1.5rem' }}>
            {recsLoading && (
              <div className="mobile-clubfitting__loading-state" role="status" aria-live="polite">
                <span className="mobile-clubfitting__spinner" />
                <span>{ctaSequence[loadingTextIndex]}</span>
              </div>
            )}
            {recsError && <p className="mobile-clubfitting__error">{recsError}</p>}
            {!recsLoading && recommendations.length === 0 && <p></p>}
            <RecommendationCards recommendations={recommendations} compare={compare || undefined} />
          </div>
        ) : null}

        <SiteFooter style={{ marginTop: '20px', width: '300px', alignSelf: 'center' }} />
      </div>
    </>
  );
};

export default ClubFittingQuiz;
